import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Spin } from "antd";
import { H5BackButton } from "../../components/common/H5BackButton";
import { h5BackButtonCssVars } from "../../components/common/H5BackButtonConfig";
import { globalModel } from "../../global/GlobalModel";
import { h5NavigateTo } from "../../utils/miniProgramEnv";
import { useSafeAreaTopPad } from "../../utils/useSafeAreaTopPad";
import CompanionRoomPage from "../../products/companion_room/CompanionRoomPage";
import { spaceHomeSubPageShellCssVars } from "../home/Config";
import { SpaceApi } from "../SpaceApi";
import { recordProductPageVisit } from "../ProductPageVisitApi";
import { SpaceModuleSubscribeGate } from "../product/SpaceModuleSubscribeGate";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import { SpaceMessageCenterApi } from "./SpaceMessageCenterApi";
import "./SpaceMessagesStyle.css";

/** 消息中心 · 群聊（全屏，无底部 Tab；过期展示购买海报） */
export const SpaceMessageGroupChatPage = observer(function SpaceMessageGroupChatPage() {
  const { spaceId, spaceProductId } = useParams<{ spaceId: string; spaceProductId: string }>();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);
  const topPad = useSafeAreaTopPad(headerRef);
  const sid = spaceId?.trim() ?? "";
  const pid = decodeURIComponent(spaceProductId?.trim() ?? "");
  const [profile, setProfile] = useState<SpaceProfile | null>(null);
  const [module, setModule] = useState<MembershipProduct | null>(null);
  const [loading, setLoading] = useState(true);

  const conversationKey = useMemo(() => (pid ? `group:${pid}` : ""), [pid]);

  useEffect(() => {
    if (!sid || !pid || !globalModel.authReady) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [profileRes, modulesRaw, subRes] = await Promise.all([
        SpaceApi.getSpaceProfile(sid),
        SpaceApi.getStartupConfig(sid),
        globalModel.isLoggedIn ? SpaceApi.getSubscriptionDetail(sid) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      const raw = modulesRaw.find((m) => m.spaceProductId === pid);
      if (!raw) {
        setModule(null);
        setProfile(profileRes);
        setLoading(false);
        return;
      }
      const sub =
        typeof subRes === "string" || !subRes
          ? null
          : subRes.items?.find((s) => s.spaceProductId === pid);
      setProfile(profileRes);
      setModule({
        ...raw,
        userSubscribed: sub?.userSubscribed ?? false,
        daysRemaining: sub?.daysRemaining ?? null,
        trialTierVisible: sub?.trialTierVisible !== false,
        purchaseBlocked: sub?.purchaseBlocked ?? false,
        purchaseBlockHint: sub?.purchaseBlockHint ?? null,
        upgradeEligible: sub?.upgradeEligible ?? false,
        upgradeSourceSpaceProductId: sub?.upgradeSourceSpaceProductId ?? null,
        upgradeSourceDisplayName: sub?.upgradeSourceDisplayName ?? null,
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [pid, sid, globalModel.authReady, globalModel.isLoggedIn]);

  useEffect(() => {
    if (!sid || !conversationKey || !globalModel.isLoggedIn) return;
    void SpaceMessageCenterApi.markRead(sid, conversationKey);
  }, [conversationKey, sid]);

  useEffect(() => {
    if (!sid || !pid || !module) return;
    void recordProductPageVisit(sid, pid);
  }, [sid, pid, module?.spaceProductId]);

  const onBack = useCallback(() => {
    if (sid && conversationKey && globalModel.isLoggedIn) {
      void SpaceMessageCenterApi.markRead(sid, conversationKey);
    }
    if (!sid) {
      navigate(-1);
      return;
    }
    h5NavigateTo(navigate, `/space/${sid}/messages`, { replace: true });
  }, [conversationKey, navigate, sid]);

  const onLoginRequest = useCallback(() => {
    if (!sid) return;
    const redirect = encodeURIComponent(
      `/space/${sid}/messages/chat/group/${encodeURIComponent(pid)}`
    );
    h5NavigateTo(navigate, `/login?redirect=${redirect}`);
  }, [navigate, pid, sid]);

  if (!sid || !/^\d{8}$/.test(sid) || !pid) {
    return <Navigate to="/" replace />;
  }

  const title = module?.displayName?.trim() || "群聊";
  const navStyle: CSSProperties | undefined =
    topPad > 0
      ? ({ "--space-message-chat-nav-safe-top-pad": `${topPad}px` } as CSSProperties)
      : undefined;

  return (
    <div
      className="space-message-chat-shell"
      style={{ ...h5BackButtonCssVars(), ...spaceHomeSubPageShellCssVars() }}
    >
      <header ref={headerRef} className="space-message-chat-shell__nav" style={navStyle}>
        <div className="space-message-chat-shell__nav-side space-message-chat-shell__nav-side--left">
          <H5BackButton
            className="space-message-chat-shell__back"
            iconClassName="h5-back-button__icon h5-back-button__icon--invert"
            onClick={onBack}
          />
        </div>
        <h1 className="space-message-chat-shell__title">{title}</h1>
        <div className="space-message-chat-shell__nav-side" aria-hidden />
      </header>
      <div className="space-message-chat-shell__main">
        {loading || !globalModel.authReady ? (
          <div className="space-messages-loading">
            <Spin />
          </div>
        ) : !module ? (
          <div className="space-messages-empty">
            <p>群聊不存在或暂不可访问</p>
          </div>
        ) : !globalModel.isLoggedIn ? (
          <div className="space-messages-empty">
            <p>请先登录</p>
            <button type="button" className="space-messages-empty__btn" onClick={onLoginRequest}>
              去登录
            </button>
          </div>
        ) : module.userSubscribed ? (
          <CompanionRoomPage
            spaceId={sid}
            spaceProductId={pid}
            productDisplayName={module.displayName}
            spaceDisplayName={profile?.name?.trim()}
            embedded
          />
        ) : (
          <SpaceModuleSubscribeGate
            spaceId={sid}
            row={module}
            isLoggedIn
            onLoginRequest={onLoginRequest}
            spaceProfile={profile}
          />
        )}
      </div>
    </div>
  );
});

export default SpaceMessageGroupChatPage;
